/**
 * Zuordnung Datei ↔ Prüfregelset.
 *
 * Bis hierher gab es dieselbe Dateityp-Normalisierung zweimal privat (Builder-Dateischritt und
 * Laufzeit-Auswahl) und keine Stelle, die eine Datei gegen die Einschränkungen **eines** Regelsets
 * prüft – die Einschränkungen wurden vor der Dateiauswahl über alle Regelsets zu einer Vereinigung
 * zusammengefasst. Für einen gemischten Lauf ist das zu grob: IDS prüft IFC, KI meist PDF/Office,
 * und jedes Regelset darf nur die Dateien bekommen, mit denen es umgehen kann.
 */

/** Kanonische Dateityp-Namen (identisch zu `ALL_FILE_TYPES` im Builder-Modell). */
export type NormalizedFileType = 'IFC' | 'PDF' | 'EXCEL' | 'WORD' | 'POWERPOINT' | 'COORDINATION' | 'UNKNOWN';

/**
 * Bringt Dateityp-Schreibweisen auf einen kanonischen Namen. Regelsets speichern teils Endungen
 * (`.ifc`), teils Typnamen (`IFC`), Projektdateien nur Typnamen.
 *
 * @returns kanonischer Name, oder `''` wenn der Wert keinem bekannten Typ entspricht
 */
export function normalizeFileTypeToken(value: unknown): string {
  const token = String(value ?? '').trim().toUpperCase();
  if (token === '.IFC' || token === 'IFC') return 'IFC';
  if (token === '.PDF' || token === 'PDF') return 'PDF';
  if (token === '.XLSX' || token === '.XLS' || token === 'EXCEL' || token === 'XLSX') return 'EXCEL';
  if (token === '.DOCX' || token === '.DOC' || token === 'WORD') return 'WORD';
  if (token === '.PPTX' || token === '.PPT' || token === 'POWERPOINT') return 'POWERPOINT';
  if (token === 'COORDINATION') return 'COORDINATION';
  if (token === 'UNKNOWN') return 'UNKNOWN';
  return '';
}

/** Minimale Sicht auf ein Regelset – bewusst nicht an den `Ruleset`-Typ gebunden. */
export interface RulesetFileConstraints {
  allowedFileTypes?: string[] | null;
  /** IDs von Dokumentenklassen oder `seed:<KEY>`-Tokens global geteilter Kopien (nicht Titel!). */
  allowedDocumentClasses?: string[] | null;
}

/** Minimale Sicht auf eine Projektdatei. */
export interface FileTypeInfo {
  fileType?: string | null;
  documentClassTitles?: string[] | null;
}

/**
 * Die vom Regelset erlaubten Dateitypen, normalisiert und dedupliziert.
 * Ein leeres Ergebnis bedeutet **keine Einschränkung** (alle Typen erlaubt).
 */
export function rulesetAllowedFileTypes(ruleset: RulesetFileConstraints | null | undefined): string[] {
  const allowed: string[] = [];
  for (const raw of ruleset?.allowedFileTypes ?? []) {
    const normalized = normalizeFileTypeToken(raw);
    if (normalized && !allowed.includes(normalized)) allowed.push(normalized);
  }
  return allowed;
}

/**
 * True, wenn die Datei zum Regelset passt.
 *
 * @param documentClassTitleById ID→Titel-Zuordnung der Dokumentenklassen. Nötig, weil das Regelset
 *   Klassen-**IDs** speichert, die Datei aber Klassen-**Titel** trägt. Fehlt die Zuordnung, wird
 *   die Klassenprüfung übersprungen statt fälschlich zu blocken.
 */
export function rulesetAcceptsFile(
  ruleset: RulesetFileConstraints | null | undefined,
  file: FileTypeInfo | null | undefined,
  documentClassTitleById?: Map<string, string>,
): boolean {
  if (!file) return false;

  const allowedTypes = rulesetAllowedFileTypes(ruleset);
  if (allowedTypes.length > 0) {
    const fileType = normalizeFileTypeToken(file.fileType);
    // Unbekannter Dateityp kann keine Typeinschränkung erfüllen.
    if (!fileType || !allowedTypes.includes(fileType)) return false;
  }

  const allowedClassIds = (ruleset?.allowedDocumentClasses ?? []).filter(Boolean);
  if (allowedClassIds.length === 0 || !documentClassTitleById) return true;

  // Lookup normalisiert wie beim Aufbau der Map (trim + lowercase): UUIDs sind ohnehin
  // kleingeschrieben, aber die seed:<KEY>-Tokens geteilter Kopien tragen Großbuchstaben.
  const allowedTitles = allowedClassIds
    .map(id => documentClassTitleById.get(String(id)) ?? documentClassTitleById.get(String(id).trim().toLowerCase()))
    .filter((title): title is string => !!title)
    .map(title => title.trim().toUpperCase());
  // Keine der IDs auflösbar → Einschränkung nicht bewertbar, deshalb nicht blocken.
  if (allowedTitles.length === 0) return true;

  const fileTitles = (file.documentClassTitles ?? [])
    .map(title => String(title ?? '').trim().toUpperCase())
    .filter(Boolean);
  if (fileTitles.length === 0) return false;
  return fileTitles.some(title => allowedTitles.includes(title));
}

/** Die Teilmenge der Dateien, die das Regelset prüfen kann. */
export function filesForRuleset<T extends FileTypeInfo>(
  ruleset: RulesetFileConstraints | null | undefined,
  files: T[],
  documentClassTitleById?: Map<string, string>,
): T[] {
  return files.filter(file => rulesetAcceptsFile(ruleset, file, documentClassTitleById));
}
